/**
 * MainVideoPanel: 발표자 뷰(PRESENTER 모드)의 메인 비디오 패널.
 *
 * v1 원본: RenewalComponents/SVContent/PresenterScreen/MainVideo/MainVideoPresenter.js
 * v2 변환: 단일 함수형 컴포넌트
 *
 * VideoPanel(청중용)과 달리:
 * - namebx가 statusBox 안에 위치
 * - sideView__funcBox (핀 고정 + 화면 자동 전환) 포함
 */
import React, { useState, useEffect } from 'react';
import VideoTile from '@/components/VideoTile/VideoTile';
import { useRemoteTracks, useLocalVideoTrack, useLocalScreenShareTrack } from '@/contexts/TrackContext';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import type { Participant } from '@/store/slices/participantsSlice';
import { isMasterUser } from '@/store/slices/masterSlice';
import { LANGUAGE_FLAG_MAP, type TranslationLanguage } from '@/store/slices/translationSlice';
import { DrawingGet } from '@/components/DrawingPanel/DrawingPanel';
import ContextMenu from '@/components/SVContent/Common/ContextMenu';
import Emoji from '@/components/SVContent/Common/Emoji';

interface MainVideoPanelProps {
  participant: Participant | 'local';
  localUserId: string;
  isUseAutoScreenSwitch: boolean;
  isPinned: boolean;
  onPinUser: (userId: string, type: 'pin' | 'unpin', isScreenShare?: boolean) => void;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onKickParticipant?: (userId: string) => void;
  /** true: 화면 공유 트랙을 표시 (카메라 대신) */
  showScreenShare?: boolean;
  /** true: 핀 버튼 숨김 (화면공유 전환 등 고정 아닌 단순 표시 시) */
  hidePinButton?: boolean;
}

const MainVideoPanel: React.FC<MainVideoPanelProps> = ({
  participant,
  localUserId,
  isUseAutoScreenSwitch,
  isPinned,
  onPinUser,
  onClick,
  onDoubleClick,
  onKickParticipant,
  showScreenShare = false,
  hidePinButton = false,
}) => {
  const dispatch = useAppDispatch();
  const local = useAppSelector((s) => s.local);
  const participants = useAppSelector((s) => s.participants);
  const masterUsers = useAppSelector((s) => s.master.masterUsers);
  const localScreenSharing = useAppSelector((s) => s.screenShare.isSharing);
  const drawing = useAppSelector((s) => s.drawing);
  const isAutoScreenSwitch = useAppSelector((s) => s.ui.isAutoScreenSwitch);
  const isTranslateEnabled = useAppSelector((s) => s.translation.isTranslateEnabled);
  const translationLanguage = useAppSelector((s) => s.translation.language);
  const translatingUsers = useAppSelector((s) => s.translation.translatingUsers);

  const isLocal = participant === 'local';
  const userId = isLocal ? localUserId : (participant as Participant).id;

  const remoteTracks = useRemoteTracks(isLocal ? '' : userId);
  const localVideoTrack = useLocalVideoTrack();
  const localScreenShareTrack = useLocalScreenShareTrack();

  const remoteP = isLocal ? null : (participant as Participant);
  const officialName = isLocal
    ? (local.userName || local.nickname || local.userId)
    : (remoteP!.displayName || remoteP!.nickname || '');
  const nickname = isLocal ? local.userName : (remoteP!.displayName || '');
  const displayName = officialName;
  const tileName = showScreenShare ? `${displayName}의 화면 공유` : displayName;
  const isMaster = isLocal
    ? isMasterUser(masterUsers, local.wehagoId)
    : isMasterUser(masterUsers, (participant as Participant).wehagoId);

  const isAudioMuted = isLocal
    ? local.audioMuted
    : (participant as Participant).tracks.audioMuted;

  const isScreenSharing = isLocal
    ? localScreenSharing
    : (participant as Participant).tracks.isScreenSharing;

  const connectionQuality = isLocal
    ? local.connectionQuality
    : (participants.participants[userId]?.connectionQuality ?? 100);

  const videoTrack = showScreenShare
    ? (isLocal ? localScreenShareTrack : remoteTracks.screenShare)
    : (isLocal ? localVideoTrack : remoteTracks.video);

  // 사용자가 의도적으로 카메라를 끈 상태 (캐릭터 아바타 표시)
  const isVideoMuted = showScreenShare
    ? false
    : isLocal
      ? local.videoMuted || !local.hasVideoTrack
      : (participant as Participant).tracks.videoMuted;

  // 카메라는 켜져있지만 아직 비디오 트랙이 도착하지 않은 상태 (연결 중 — 엠티셋 표시)
  const isVideoConnecting = !isVideoMuted && !videoTrack;

  // 비디오 첫 프레임 수신 여부
  const [isVideoReady, setIsVideoReady] = useState(false);
  useEffect(() => { setIsVideoReady(false); }, [videoTrack]);

  // 기존 isCameraOff: 의도적 카메라 OFF만 의미 (캐릭터 표시용)
  const isCameraOff = isVideoMuted;

  const showEmptyOverlay = !isCameraOff && videoTrack && !isVideoReady;

  const effectiveJitsiId = isLocal ? local.jitsiId : (participant as Participant).id;
  const isVideoPanelSketchOwner = drawing.reduxIsSketchShare && drawing.reduxSketchOwner === effectiveJitsiId;

  const getCharacterClass = (customAvatar?: string) => {
    if (customAvatar === 'jangok') return 'item--guest item--guestCrt1';
    if (customAvatar === 'suzy') return 'item--guest item--guestCrt2';
    if (customAvatar === 'jessie') return 'item--guest item--guestCrt3';
    return '';
  };
  const selectedAvatar = isLocal ? local.selectedAvatar : (participant as Participant).selectedAvatar;
  const characterClass = isCameraOff && !isVideoPanelSketchOwner ? getCharacterClass(selectedAvatar) : '';

  const getQualityLevel = (quality: number): 'good' | 'normal' | 'bad' => {
    if (quality >= 40) return 'good';
    if (quality >= 20) return 'normal';
    return 'bad';
  };
  const qualityLevel = getQualityLevel(connectionQuality);
  const shouldShowNetworkStatus = qualityLevel !== 'good';

  const tileId = showScreenShare ? `video-item-${userId}-screen` : `video-item-${userId}`;

  return (
    <div
      id={tileId}
      className={['item', characterClass, showScreenShare ? 'item--screenShare' : ''].filter(Boolean).join(' ')}
      onDoubleClick={onDoubleClick}
    >
      {/* 리액션 레이어 (마크업 기준 타일 최상단 배치) */}
      {/* <Emoji userId={isLocal ? local.wehagoId : (participant as Participant).wehagoId || userId} /> */}

      <button
        type="button"
        className={`movbx${(isVideoConnecting || showEmptyOverlay) ? ' empty' : ''}`}
        style={{ overflow: 'hidden' }}
        onClick={onClick}
      >
        {!isCameraOff && videoTrack ? (
          <VideoTile
            track={videoTrack}
            isLocal={isLocal}
            isDesktop={showScreenShare}
            isReverse={isLocal ? local.isVideoReverse : false}
            onReady={setIsVideoReady}
          />
        ) : null}

        {isVideoPanelSketchOwner && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
            <DrawingGet
              sketchData={drawing.reduxSketchData ?? {}}
              sketchWidthLimit={drawing.reduxSketchWidthLimit}
              sketchHeightLimit={drawing.reduxSketchHeightLimit}
            />
          </div>
        )}
      </button>

      {/* namebx — 마크업: movbx 다음 */}
      <div className="namebx">
        {!showScreenShare && (
          <button
            type="button"
            className={`btnMic${isAudioMuted ? ' off' : ''}`}
          >
            <span className="blind">마이크</span>
          </button>
        )}
        <strong className="name ellipsis" title={tileName}>
          {tileName}
          {isLocal && !showScreenShare && '(나)'}
        </strong>
        {isTranslateEnabled && (() => {
          const lang = isLocal
            ? translationLanguage
            : translatingUsers[userId]?.language
              ?? (remoteP?.nationality as TranslationLanguage | undefined);
          const flagSrc = lang && LANGUAGE_FLAG_MAP[lang as TranslationLanguage];
          return flagSrc ? <img className="namebx__flagIcon" src={flagSrc} alt="국기이미지" /> : null;
        })()}
      </div>

      {/* statusBox — 마크업: namebx 다음 */}
      <div className="statusBox">
        {isMaster && (
          <span className="statusBox_master">
            <span className="blind">마스터</span>
          </span>
        )}
        {shouldShowNetworkStatus && (
          <span className={`statusBox_network ${qualityLevel}`}>
            <span className="blind">네트워크 상태 : {qualityLevel === 'normal' ? '보통' : '나쁨'}</span>
          </span>
        )}
      </div>

      {/* v1 sideView__funcBox: 핀 고정 + 화면 자동 전환 */}
      <div className="sideView__funcBox" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
        {!hidePinButton && (
          <button
            type="button"
            className={`sideView__funcBox_pinBtn${isPinned ? ' active' : ''}`}
            onClick={() => onPinUser(userId, isPinned ? 'unpin' : 'pin', showScreenShare)}
          >
            <span className="blind">고정</span>
          </button>
        )}
        {isUseAutoScreenSwitch && (
          <div className="sideView__funcBox_autoSwitch">
            <span className="sideView__funcBox_autoSwitchText">화면 자동 전환</span>
            <div className="common_onoff_switch small2 type_blue2">
              <label>
                <input
                  className="onoff_input"
                  type="checkbox"
                  checked={isAutoScreenSwitch}
                  onChange={() => {
                    dispatch(uiActions.setAutoScreenSwitch(!isAutoScreenSwitch));
                    dispatch(uiActions.setPresenterOverrideUserId(null));
                  }}
                />
                <span className="switch_img"></span>
                <span className="label_text on"></span>
                <span className="label_text off"></span>
              </label>
            </div>
          </div>
        )}
      </div>

      <ContextMenu
        userId={userId}
        containerId={tileId}
        onKickParticipant={onKickParticipant}
        isScreenShare={showScreenShare}
      />
    </div>
  );
};

export default MainVideoPanel;
